import { Base64 } from "js-base64"
import { type NestClient, connectNestClient, isNestApiAvailable, requestNestMethod } from "../nest/client"
import type { OutputChunk, RuntimeRecord } from "../nest/generated"
import { isCodeModuleAvailable } from "./capabilities"
import { OutputChunkOrderer } from "./outputOrdering"
import { type RuntimeWorkspaceContext, nestRuntimeParamsForCwd, requireRuntimeWorkspaceContext } from "./runtimeContext"

export interface SpawnParams {
    ptyId: string
    cwd: string
    env?: Record<string, string>
    cols: number
    rows: number
    context?: RuntimeWorkspaceContext
}

export interface PtyOutputEvent {
    data: string
    timestamp: number
}

export interface PtyExitEvent {
    exitCode: number | null
}

interface TerminalStatusLike {
    runtimeId?: string
    terminalId?: string
    status?: string
    exitCode?: number
}

interface TerminalOutputLike {
    runtimeId?: string
    terminalId?: string
    chunk?: OutputChunk
}

type EventHandler = (...args: unknown[]) => void

async function requireNestClient(): Promise<NestClient> {
    return connectNestClient()
}

function terminalIdFromRecord(record: RuntimeRecord | TerminalStatusLike): string {
    const terminalId = record.terminalId || record.runtimeId
    if (!terminalId) throw new Error("Nest terminal record is missing an id")
    return terminalId
}

function parseTimestamp(timestamp: string): number {
    const parsed = Date.parse(timestamp)
    return Number.isFinite(parsed) ? parsed : Date.now()
}

function encodeTerminalChunk(chunk: OutputChunk): PtyOutputEvent {
    return {
        data: chunk.encoding === "base64" ? chunk.data : Base64.encode(chunk.data),
        timestamp: parseTimestamp(chunk.timestamp),
    }
}

function isTerminalStatus(status: string): boolean {
    return status === "completed" || status === "failed" || status === "stopped"
}

function matchesTerminal(params: unknown, terminalId: string): params is TerminalStatusLike | TerminalOutputLike {
    if (typeof params !== "object" || params === null) return false
    const record = params as Record<string, unknown>
    return record.terminalId === terminalId || record.runtimeId === terminalId
}

function requireTerminalContext(context: RuntimeWorkspaceContext | undefined): RuntimeWorkspaceContext {
    return requireRuntimeWorkspaceContext(context, "Nest terminal execution")
}

export class PtyHandle {
    private _ptyId: string
    private _terminalId: string
    private _exited = false
    private _exitCode: number | null = null
    private orderer = new OutputChunkOrderer()
    private outputHistory: PtyOutputEvent[] = []
    private exitEvent: PtyExitEvent | null = null
    private listeners: Map<string, EventHandler[]> = new Map()
    private unsubscribers: Array<() => void> = []

    private constructor(ptyId: string, terminalId: string) {
        this._ptyId = ptyId
        this._terminalId = terminalId
    }

    get ptyId(): string {
        return this._ptyId
    }

    get exited(): boolean {
        return this._exited
    }

    get exitCode(): number | null {
        return this._exitCode
    }

    static async spawn(params: SpawnParams): Promise<PtyHandle | null> {
        const context = requireTerminalContext(params.context)
        console.debug("[PtyHandle] Spawning Nest terminal:", params.ptyId, params.cwd)

        const response = await requestNestMethod("terminal.spawn", {
            ...nestRuntimeParamsForCwd(params.cwd, context),
            env: params.env,
            cols: params.cols,
            rows: params.rows,
            clientRequestId: params.ptyId,
            label: params.ptyId,
        })
        const handle = new PtyHandle(params.ptyId, terminalIdFromRecord(response.terminal))
        await handle.setupListeners()
        await handle.replayOutput()
        handle.applyRecordStatus(response.terminal)

        console.debug("[PtyHandle] Nest terminal spawned:", params.ptyId, handle._terminalId)
        return handle
    }

    static async reconnect(ptyId: string, context?: RuntimeWorkspaceContext): Promise<{ handle: PtyHandle | null; found: boolean }> {
        const runtimeContext = requireTerminalContext(context)
        console.debug("[PtyHandle] Reconnecting to Nest terminal:", ptyId)

        const result = await requestNestMethod("terminal.list", { workspaceId: runtimeContext.workspaceId })
        const record = result.terminals.find((terminal) => {
            const clientRequestId = typeof terminal.data?.clientRequestId === "string" ? terminal.data.clientRequestId : null
            return clientRequestId === ptyId || terminal.terminalId === ptyId || terminal.runtimeId === ptyId
        })
        if (!record) {
            console.debug("[PtyHandle] Nest terminal not found")
            return { handle: null, found: false }
        }
        if (record.worktreeId !== runtimeContext.worktreeId) {
            if (record.status === "starting" || record.status === "running") {
                await requestNestMethod("terminal.stop", { terminalId: terminalIdFromRecord(record) })
            }
            console.debug("[PtyHandle] Ignoring terminal from a different worktree")
            return { handle: null, found: false }
        }

        const handle = new PtyHandle(ptyId, terminalIdFromRecord(record))
        await handle.setupListeners()
        await handle.replayOutput()
        handle.applyRecordStatus(record)
        return { handle, found: true }
    }

    private async setupListeners(): Promise<void> {
        const client = await requireNestClient()
        await client.addSubscriptions([
            { method: "terminal.output", terminalId: this._terminalId },
            { method: "terminal.exited", terminalId: this._terminalId },
            { method: "terminal.stopped", terminalId: this._terminalId },
        ])

        this.unsubscribers.push(
            client.on("terminal.output", (params) => {
                if (!matchesTerminal(params, this._terminalId)) return
                const chunk = params.chunk
                if (chunk) this.handleOutputChunk(chunk)
            }),
            client.on("terminal.exited", (params) => {
                if (!matchesTerminal(params, this._terminalId)) return
                this.applyRecordStatus(params)
            }),
            client.on("terminal.stopped", (params) => {
                if (!matchesTerminal(params, this._terminalId)) return
                this.applyRecordStatus(params)
            })
        )
    }

    private async replayOutput(): Promise<void> {
        for (;;) {
            const result = await requestNestMethod("terminal.read", {
                terminalId: this._terminalId,
                offset: this.orderer.readOffset,
                maxBytes: 1024 * 1024,
            })
            if (result.droppedBeforeOffset != null) this.orderer.skipTrimmed(result.droppedBeforeOffset)
            for (const chunk of result.chunks) {
                this.handleOutputChunk(chunk)
            }
            if (!result.truncated || result.chunks.length === 0) break
        }
    }

    private handleOutputChunk(chunk: OutputChunk): void {
        this.orderer.accept(chunk, (ordered) => {
            const normalized = encodeTerminalChunk(ordered)
            this.outputHistory.push(normalized)
            this.emit("output", normalized)
        })
    }

    private applyRecordStatus(record: TerminalStatusLike): void {
        const status = record.status
        if (!status || !isTerminalStatus(status) || this._exited) return
        this._exited = true
        this._exitCode = record.exitCode ?? null
        this.exitEvent = { exitCode: this._exitCode }
        this.emit("exit", this.exitEvent)
    }

    private emit(event: string, ...args: unknown[]): void {
        const handlers = this.listeners.get(event) || []
        handlers.forEach((handler) => handler(...args))
    }

    on(event: "output" | "exit", handler: EventHandler): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, [])
        }
        this.listeners.get(event)!.push(handler)
        if (event === "output") {
            // Flush buffered history synchronously so it cannot interleave behind a
            // live chunk that emits before a deferred replay would have run.
            for (const chunk of this.outputHistory) {
                handler(chunk)
            }
        }
        if (event === "exit" && this.exitEvent) {
            queueMicrotask(() => handler(this.exitEvent))
        }
    }

    off(event: string, handler: EventHandler): void {
        const handlers = this.listeners.get(event)
        if (handlers) {
            const idx = handlers.indexOf(handler)
            if (idx >= 0) handlers.splice(idx, 1)
        }
    }

    async write(data: string): Promise<void> {
        if (this._exited) return
        await requestNestMethod("terminal.write", { terminalId: this._terminalId, data: Base64.encode(data), encoding: "base64" })
    }

    async resize(cols: number, rows: number): Promise<void> {
        if (this._exited) return
        await requestNestMethod("terminal.resize", { terminalId: this._terminalId, cols, rows })
    }

    async kill(): Promise<void> {
        await requestNestMethod("terminal.stop", { terminalId: this._terminalId })
        this._exited = true
        this.cleanup()
    }

    cleanup(): void {
        for (const unsub of this.unsubscribers) {
            unsub()
        }
        this.unsubscribers = []
    }
}

function isPtyApiAvailable(): boolean {
    return isCodeModuleAvailable() && isNestApiAvailable()
}

/** Derives the ptyId for a task's terminal. Currently 1:1 with taskId. */
export function getTaskPtyId(taskId: string): string {
    return taskId
}

async function killPty(ptyId: string, context?: RuntimeWorkspaceContext): Promise<void> {
    const runtimeContext = requireTerminalContext(context)
    const result = await requestNestMethod("terminal.list", { workspaceId: runtimeContext.workspaceId })
    const record = result.terminals.find((terminal) => {
        const clientRequestId = typeof terminal.data?.clientRequestId === "string" ? terminal.data.clientRequestId : null
        return clientRequestId === ptyId || terminal.terminalId === ptyId || terminal.runtimeId === ptyId
    })
    if (!record) return
    await requestNestMethod("terminal.stop", { terminalId: terminalIdFromRecord(record) })
}

async function killAllPtys(workspaceId = ""): Promise<boolean> {
    const result = await requestNestMethod("terminal.list", { workspaceId })
    const live = result.terminals.filter((terminal) => terminal.status === "starting" || terminal.status === "running")
    await Promise.all(live.map((terminal) => requestNestMethod("terminal.stop", { terminalId: terminalIdFromRecord(terminal) })))
    return true
}

export const ptyApi = {
    spawn: PtyHandle.spawn,
    reconnect: PtyHandle.reconnect,
    isAvailable: isPtyApiAvailable,
    kill: killPty,
    killAll: killAllPtys,
}
